import { notify, useModal } from '@billy/design-system'
import isEmpty from 'lodash/isEmpty'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { useForm, UseFormReturn } from '../../../../../../../hooks/useForm'
import { usePredefinedPaymentMethodsValues } from '../../../hooks/usePredefinedPaymentMethodsValues'
import { PaymentMethodsFormSchema } from '../../../types/paymentMethods/paymentMethodsFormSchema'
import { getPaymentMethodsFormSchema } from '../../../utils/getPaymentMethodsFormSchema'
import { useUpdatePaymentMethods } from '../hooks/useUpdatePaymentMethods'
import { PaymentMethodsForm } from '../types/PaymentMethodsForm'
import { getDefaultValues } from '../utils/formData'

interface ContextState {
  closeModal: () => void
  isLoading: boolean
  isProcessing: boolean
  paymentMethodsForm: UseFormReturn<PaymentMethodsFormSchema>
  submitForm: () => void
}

const PaymentMethodsFormContext = createContext<ContextState | undefined>(undefined)

interface PaymentMethodsFormContextProviderProps {
  children?: ReactNode
  id: string
  onSubmit?: () => void
}

export const PaymentMethodsFormContextProvider = ({
  children,
  id,
  onSubmit,
}: PaymentMethodsFormContextProviderProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeModal } = useModal(id)

  const { isLoading: isLoadingPredefinedValues, predefinedPaymentMethodsValues } = usePredefinedPaymentMethodsValues()

  const paymentMethodsForm = useForm<PaymentMethodsFormSchema>({
    defaultValues: getDefaultValues(),
    validationSchema: getPaymentMethodsFormSchema(t),
  })

  const { getValues, handleSubmit, reset: resetForm } = paymentMethodsForm

  const { update: updatePaymentMethods, isProcessing } = useUpdatePaymentMethods({
    onSuccess: () => {
      onSubmit?.()
      closeModal()
    },
  })

  const handlePaymentMethodsFormSubmit = useCallback(
    (formData: PaymentMethodsForm) => {
      const allEmptyFields = Object.values(getValues()).every((val) => isEmpty(val))

      if (allEmptyFields) {
        notify({
          id: NotificationKeys.ExternalInvoiceSettingsUpdate,
          message: t('external_invoices.payment_methods_modal.validation.empty_fields'),
          variant: 'error',
        })
        return
      }

      updatePaymentMethods(formData)
    },
    [getValues, updatePaymentMethods, t],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handlePaymentMethodsFormSubmit)()
  }, [handleSubmit, handlePaymentMethodsFormSubmit])

  useEffect(() => {
    if (predefinedPaymentMethodsValues && !isLoadingPredefinedValues) {
      const updatedFormValues = getDefaultValues(predefinedPaymentMethodsValues)
      resetForm(updatedFormValues)
    }
  }, [isLoadingPredefinedValues, predefinedPaymentMethodsValues, resetForm])

  const contextValue = useMemo(
    () => ({ closeModal, isLoading: isLoadingPredefinedValues, isProcessing, paymentMethodsForm, submitForm }),
    [closeModal, isLoadingPredefinedValues, isProcessing, paymentMethodsForm, submitForm],
  )

  return <PaymentMethodsFormContext.Provider value={contextValue}>{children}</PaymentMethodsFormContext.Provider>
}

export const usePaymentMethodsForm = () => {
  const context = useContext(PaymentMethodsFormContext)

  if (!context) {
    throw new Error('PaymentMethodsFormContextProvider is missing in the module!')
  }

  return context
}
